/**
 * Renders a string that may contain inline HTML formatting
 * (bold, italic, underline, links) produced by TipTap.
 *
 * If the string contains no HTML tags, it renders as a plain text node
 * to avoid unnecessary DOM wrappers. When HTML is detected, it is
 * sanitized through a strict allowlist before rendering via
 * dangerouslySetInnerHTML. The allowlist matches TipTap's output schema.
 *
 * sanitize-html is a pure string-based parser (no DOM required), so it
 * works identically in Node SSR (frontend) and browser SPA (portal).
 */

import sanitizeHtml from "sanitize-html";
import { RICH_TEXT_SANITIZE_OPTIONS } from "./sanitize";

const HTML_TAG_RE = /<[a-z][\s\S]*>/i;

interface RichTextProps {
  html: string;
  as?: keyof React.JSX.IntrinsicElements;
  className?: string;
  /** Extra attributes to spread onto the wrapper element. */
  [key: `data-${string}`]: string | undefined;
}

export function RichText({
  html,
  as: Tag = "span",
  className,
  ...rest
}: RichTextProps) {
  const cls = className ? `rich-text ${className}` : "rich-text";
  if (HTML_TAG_RE.test(html)) {
    const clean = sanitizeHtml(html, RICH_TEXT_SANITIZE_OPTIONS);
    return (
      <Tag
        className={cls}
        dangerouslySetInnerHTML={{ __html: clean }}
        {...rest}
      />
    );
  }
  return (
    <Tag className={cls} {...rest}>
      {html}
    </Tag>
  );
}
